/**
 * StockFinderModal — card-time live stock fetch (Architecture B).
 *
 * Operator-triggered, parallel path to the Art Director recommender (which
 * stays focused on Remi's own photos). When a card wants a *scene* rather than
 * a person, this queries Unsplash + Pexels LIVE for the card's topic via the
 * existing `GET /stock/search` engine, shows results, and on click attaches
 * the picked image straight to the card's hero (via the parent's
 * select-candidate handler). No held library, no classification, no pool
 * write — pure live fetch.
 *
 * The query is pre-filled from the card but fully editable before searching
 * (operators often want something more evocative than the literal card text).
 */
import React, { useEffect, useRef, useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import { Skeleton } from "./ui/skeleton";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import {
  Search as SearchIcon,
  Loader2,
  ImageOff,
  Camera,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

const _API_BASE =
  typeof window !== "undefined" && window.location.hostname !== "localhost"
    ? window.location.origin
    : process.env.REACT_APP_BACKEND_URL ||
      (typeof window !== "undefined" ? window.location.origin : "");
const API = `${_API_BASE}/api`;

const SOURCE_PILL = {
  unsplash: "bg-black text-white border-0",
  pexels: "bg-teal-600 text-white border-0",
};

function ResultTile({ candidate, attaching, onAttach }) {
  const [broken, setBroken] = useState(false);
  const key = `${candidate.source}:${candidate.source_id}`;
  const busy = attaching === key;
  return (
    <div
      className="relative group rounded-lg border border-slate-200 bg-white overflow-hidden hover:border-indigo-400 transition"
      data-testid={`stock-finder-result-${key}`}
    >
      <button
        type="button"
        disabled={!!attaching}
        onClick={() => onAttach(candidate)}
        className="block w-full aspect-square bg-slate-100 cursor-pointer disabled:cursor-wait"
        data-testid={`stock-finder-attach-${key}`}
        title="Use this image for the card"
      >
        {broken || !candidate.thumb_url ? (
          <div className="w-full h-full flex items-center justify-center text-slate-400">
            <ImageOff className="h-8 w-8" />
          </div>
        ) : (
          <img
            src={candidate.thumb_url}
            alt={candidate.description || candidate.source_id}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={() => setBroken(true)}
          />
        )}
        {/* Hover/attach overlay */}
        <div
          className={`absolute inset-0 flex items-center justify-center transition ${
            busy ? "bg-black/50 opacity-100" : "bg-black/40 opacity-0 group-hover:opacity-100"
          }`}
        >
          {busy ? (
            <span className="text-white text-sm flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Attaching…
            </span>
          ) : (
            <span className="text-white text-sm font-medium">Use this image</span>
          )}
        </div>
      </button>

      <div className="absolute top-2 left-2">
        <Badge className={`${SOURCE_PILL[candidate.source] || "bg-slate-700 text-white border-0"} text-[10px]`}>
          <Camera className="h-2.5 w-2.5 mr-1" />
          {candidate.source === "unsplash" ? "Unsplash" : "Pexels"}
        </Badge>
      </div>

      <div className="p-2 text-[11px] space-y-0.5">
        <div className="truncate text-slate-600 italic" title={candidate.attribution_text}>
          {candidate.attribution_text}
        </div>
        {candidate.source_url ? (
          <a
            href={candidate.source_url}
            target="_blank"
            rel="noreferrer noopener"
            onClick={(e) => e.stopPropagation()}
            className="text-indigo-600 hover:underline inline-flex items-center gap-1"
          >
            <ExternalLink className="h-2.5 w-2.5" /> View source
          </a>
        ) : null}
      </div>
    </div>
  );
}

export default function StockFinderModal({ workspaceId, initialQuery, onAttach, onClose }) {
  const [query, setQuery] = useState(initialQuery || "");
  const [sources, setSources] = useState({ unsplash: true, pexels: true });
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null); // {candidates,...} | null
  const [attaching, setAttaching] = useState(null); // candidate key | null
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, []);

  const runSearch = async (e) => {
    if (e) e.preventDefault();
    const q = query.trim();
    if (!q) {
      toast.warning("Enter something to search for");
      return;
    }
    const srcList = Object.entries(sources).filter(([, on]) => on).map(([k]) => k);
    if (srcList.length === 0) {
      toast.warning("Select at least one source");
      return;
    }
    setLoading(true);
    try {
      const url = `${API}/v2/workspaces/${workspaceId}/stock/search?q=${encodeURIComponent(q)}&sources=${srcList.join(",")}&per_page=24`;
      const r = await fetch(url, { credentials: "include" });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail?.message || data?.detail || `HTTP ${r.status}`);
      setResults(data);
      if ((data.candidates || []).length === 0) {
        toast.message("No results", { description: "Try a different query." });
      }
      if ((data.provider_errors || []).length > 0) {
        toast.warning(`Provider issue(s): ${data.provider_errors.map((x) => `${x.source}: ${x.error}`).join("; ")}`);
      }
      if ((data.capped_sources || []).length > 0) {
        toast.warning(`Capped this month: ${data.capped_sources.join(", ")}`);
      }
    } catch (err) {
      toast.error(`Search failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAttach = async (candidate) => {
    const key = `${candidate.source}:${candidate.source_id}`;
    setAttaching(key);
    try {
      await onAttach(candidate);
      onClose();
    } catch (err) {
      toast.error(err?.message || "Could not attach image");
    } finally {
      setAttaching(null);
    }
  };

  const candidates = results?.candidates || [];

  // Rendered as a Radix Dialog. The drawer that invokes this modal is itself a
  // Radix <Sheet> (modal): it uses a CSS `transform` (which would trap a plain
  // `position: fixed` overlay inside the drawer panel) AND sets
  // `body { pointer-events: none }` + a focus-trap that make any out-of-tree
  // overlay non-interactive. Using Radix's own Dialog lets it join Radix's
  // layer stack — it portals out (escaping the transform), is interactive, and
  // takes over the focus trap from the drawer so the search box is typeable.
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="max-w-4xl w-full p-0 gap-0 max-h-[88vh] overflow-hidden flex flex-col"
        data-testid="stock-finder-modal"
      >
        {/* Header */}
        <div className="p-4 border-b border-slate-200">
          <DialogTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <SearchIcon className="h-4 w-4 text-indigo-600" /> Find a stock image
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-0.5">
            Live search across Unsplash + Pexels. Click any result to set it as the card's hero.
          </p>
        </div>

        {/* Search form */}
        <form onSubmit={runSearch} className="p-4 border-b border-slate-200 space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="e.g. resilient leadership, lagos skyline, team collaboration"
                className="pl-9"
                data-testid="stock-finder-input"
              />
            </div>
            <Button type="submit" disabled={loading} data-testid="stock-finder-search-btn">
              {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <SearchIcon className="h-4 w-4 mr-1" />}
              Search
            </Button>
          </div>
          <div className="flex items-center gap-4 text-xs flex-wrap">
            <span className="text-slate-500">Sources:</span>
            {["unsplash", "pexels"].map((s) => (
              <label key={s} className="flex items-center gap-1.5 cursor-pointer" data-testid={`stock-finder-source-${s}`}>
                <Checkbox checked={sources[s]} onCheckedChange={(v) => setSources({ ...sources, [s]: !!v })} />
                <span className="capitalize">{s}</span>
              </label>
            ))}
            <span className="text-slate-400">Prefilled from the card — edit for a more evocative search.</span>
          </div>
        </form>

        {/* Results */}
        <div className="p-4 overflow-auto" data-testid="stock-finder-results">
          {loading ? (
            <div className="space-y-3">
              <div className="text-xs text-slate-500 flex items-center gap-2" data-testid="stock-finder-loading-hint">
                <Loader2 className="h-3 w-3 animate-spin" />
                Searching Unsplash + Pexels live — this can take 5-15s…
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} className="aspect-square rounded-lg" />
                ))}
              </div>
            </div>
          ) : results === null ? (
            <div className="border border-dashed border-slate-300 rounded-lg p-10 text-center text-slate-500 text-sm">
              Search above to find a scene for this card. Unsplash + Pexels have millions of images — the whole catalogue, per card.
            </div>
          ) : candidates.length === 0 ? (
            <div className="border border-dashed border-slate-300 rounded-lg p-10 text-center text-slate-500 text-sm" data-testid="stock-finder-no-results">
              No results for "{results.query}". Try a different query.
            </div>
          ) : (
            <>
              <div className="text-xs text-slate-500 mb-3">
                {results.count} result{results.count === 1 ? "" : "s"} · click one to use it
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {candidates.map((c) => (
                  <ResultTile
                    key={`${c.source}:${c.source_id}`}
                    candidate={c}
                    attaching={attaching}
                    onAttach={handleAttach}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
